/**
 * TAHLEEL.ai — Tactical Readout Panel
 *
 * Dark card showing live tactical data from the latest analysis:
 * - Mini football pitch with formation dots
 * - Formation, press intensity, defensive line, width index
 * - Key weakness, recommended counter
 *
 * Pulls real data from the latest Supabase analysis.
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../services/api';

const TacticalReadout = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLatestAnalysis();
  }, []);

  const loadLatestAnalysis = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: analyses, error } = await supabase
        .from('analyses')
        .select('analysis_data, opponent_team, created_at, confidence_score')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);

      if (!error && analyses && analyses.length > 0) {
        const latest = analyses[0];
        const ad = latest.analysis_data || {};
        const tactical = ad.tactical || {};
        const indices = tactical.tactical_indices || ad.tacticalIndices || {};
        const formation = tactical.inferred_formation || ad.inferredFormation || '';
        const formConf = tactical.formation_confidence || ad.formationConfidence || '';
        const zoneMap = tactical.zone_distribution || ad.zoneDistribution || {};

        // Find key weakness from zone data
        let keyWeakness = 'N/A';
        if (zoneMap) {
          const leftDef = (zoneMap.left_defensive || 0) + (zoneMap.left_midfield || 0);
          const rightDef = (zoneMap.right_defensive || 0) + (zoneMap.right_midfield || 0);
          if (leftDef < rightDef * 0.6) keyWeakness = 'LEFT';
          else if (rightDef < leftDef * 0.6) keyWeakness = 'RIGHT';
          else keyWeakness = 'BALANCED';
        }

        // Infer counter formation
        let counter = '4-4-2';
        if (formation.includes('4-3-3')) counter = '4-2-3-1';
        else if (formation.includes('4-4-2')) counter = '4-3-3';
        else if (formation.includes('4-2-3-1')) counter = '3-5-2';
        else if (formation.includes('3-5-2') || formation.includes('3-4-3')) counter = '4-4-2';
        else if (formation.includes('5-4-1') || formation.includes('5-3-2')) counter = '4-3-3';

        setData({
          opponent: latest.opponent_team || 'Last Match',
          formation: formation || 'N/A',
          formationConfidence: formConf || 'N/A',
          pressIntensity: indices.pressure_index != null ? Math.round(indices.pressure_index * 100) : null,
          defensiveLine: indices.defensive_line_height != null ? Math.round(indices.defensive_line_height * 100) : null,
          widthIndex: indices.width_index != null ? indices.width_index.toFixed(2) : null,
          compactness: indices.compactness != null ? indices.compactness.toFixed(2) : null,
          keyWeakness,
          counter,
          confidence: latest.confidence_score || 0,
          date: latest.created_at,
        });
      }
    } catch (err) {
      console.error('TacticalReadout: failed to load', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="rounded-2xl p-6 animate-pulse" style={{ background: '#0a1628', border: '1px solid #1e3a5f' }}>
        <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
        <div className="h-40 bg-gray-800 rounded mb-4"></div>
        <div className="space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="h-3 bg-gray-700 rounded"></div>)}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl p-6 text-center" style={{ background: '#0a1628', border: '1px solid #1e3a5f' }}>
        <p style={{ color: '#64748b', fontSize: 13 }}>No analysis data yet. Upload a match video to see tactical readout.</p>
      </div>
    );
  }

  // Formation dot positions (normalized 0-100)
  const FORMATIONS = {
    '4-3-3': [
      {x:50,y:90},{x:15,y:72},{x:38,y:72},{x:62,y:72},{x:85,y:72},
      {x:25,y:50},{x:50,y:48},{x:75,y:50},{x:20,y:28},{x:50,y:22},{x:80,y:28}
    ],
    '4-4-2': [
      {x:50,y:90},{x:15,y:72},{x:38,y:72},{x:62,y:72},{x:85,y:72},
      {x:15,y:48},{x:38,y:48},{x:62,y:48},{x:85,y:48},{x:38,y:25},{x:62,y:25}
    ],
    '4-2-3-1': [
      {x:50,y:90},{x:15,y:72},{x:38,y:72},{x:62,y:72},{x:85,y:72},
      {x:35,y:55},{x:65,y:55},{x:20,y:38},{x:50,y:35},{x:80,y:38},{x:50,y:20}
    ],
    '3-5-2': [
      {x:50,y:90},{x:25,y:72},{x:50,y:72},{x:75,y:72},
      {x:10,y:48},{x:30,y:48},{x:50,y:45},{x:70,y:48},{x:90,y:48},{x:38,y:25},{x:62,y:25}
    ],
  };

  const formKey = Object.keys(FORMATIONS).find(k => data.formation.includes(k)) || '4-4-2';
  const dots = FORMATIONS[formKey] || FORMATIONS['4-4-2'];

  const pressLevel = data.pressIntensity != null
    ? (data.pressIntensity > 70 ? 'HIGH' : data.pressIntensity > 40 ? 'MED' : 'LOW')
    : null;

  const defLevel = data.defensiveLine != null
    ? (data.defensiveLine < 30 ? 'DEEP' : data.defensiveLine > 60 ? 'HIGH' : 'MID')
    : null;

  const widthLevel = data.widthIndex != null
    ? (parseFloat(data.widthIndex) > 0.6 ? 'WIDE' : parseFloat(data.widthIndex) < 0.3 ? 'NARROW' : 'MED')
    : null;

  const rows = [
    { label: 'FORMATION', value: data.formation, tag: 'DETECTED', tagColor: '#22c55e', tagBg: 'rgba(34,197,94,0.15)' },
    pressLevel && { label: 'PRESS INTENSITY', value: `${data.pressIntensity}%`, tag: pressLevel, tagColor: pressLevel === 'HIGH' ? '#f59e0b' : '#60a5fa', tagBg: pressLevel === 'HIGH' ? 'rgba(245,158,11,0.15)' : 'rgba(96,165,250,0.15)' },
    defLevel && { label: 'DEFENSIVE LINE', value: `${data.defensiveLine}m`, tag: defLevel, tagColor: '#60a5fa', tagBg: 'rgba(96,165,250,0.15)' },
    widthLevel && { label: 'WIDTH INDEX', value: data.widthIndex, tag: widthLevel, tagColor: '#a855f7', tagBg: 'rgba(168,85,247,0.15)' },
    { label: 'KEY WEAKNESS', value: data.keyWeakness, tag: data.keyWeakness !== 'BALANCED' ? 'EXPOSED' : 'STABLE', tagColor: data.keyWeakness !== 'BALANCED' ? '#ef4444' : '#22c55e', tagBg: data.keyWeakness !== 'BALANCED' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)' },
    { label: 'RECOMMENDED', value: data.counter, tag: 'COUNTER', tagColor: '#f59e0b', tagBg: 'rgba(245,158,11,0.15)' },
  ].filter(Boolean);

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0a1628', border: '1px solid rgba(245,158,11,0.15)', boxShadow: '0 0 30px rgba(245,158,11,0.03)' }}>

      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#f59e0b', letterSpacing: 1 }}>
          &#9917; TAHLEEL AI ENGINE
        </span>
        <span style={{ fontSize: 11, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 1.5s infinite' }}></span>
          LIVE DATA
        </span>
      </div>

      {/* Mini Pitch */}
      <div className="mx-4 mt-4 mb-2 relative" style={{ height: 170, background: 'linear-gradient(#1a7a3c, #1d8a42 50%, #1a7a3c)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        {/* Pitch lines */}
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.25)' }}></div>
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 40, height: 40, border: '1px solid rgba(255,255,255,0.25)', borderRadius: '50%', transform: 'translate(-50%,-50%)' }}></div>
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: 4, height: 4, background: 'rgba(255,255,255,0.4)', borderRadius: '50%', transform: 'translate(-50%,-50%)' }}></div>
        {/* Penalty boxes */}
        <div style={{ position: 'absolute', bottom: 0, left: '50%', width: '50%', height: '18%', border: '1px solid rgba(255,255,255,0.2)', borderBottom: 'none', transform: 'translateX(-50%)' }}></div>
        <div style={{ position: 'absolute', top: 0, left: '50%', width: '50%', height: '18%', border: '1px solid rgba(255,255,255,0.2)', borderTop: 'none', transform: 'translateX(-50%)' }}></div>

        {/* Formation dots */}
        {dots.map((dot, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${dot.x}%`, top: `${dot.y}%`,
            width: 8, height: 8, borderRadius: '50%',
            background: '#f59e0b',
            boxShadow: '0 0 8px rgba(245,158,11,0.5)',
            transform: 'translate(-50%,-50%)',
            animation: `fadeIn 0.4s ${i * 0.06}s both`,
          }}></div>
        ))}

        {/* Formation labels */}
        <span style={{ position: 'absolute', bottom: 6, left: 10, fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#f59e0b', letterSpacing: 1 }}>
          {data.formation}
        </span>
        <span style={{ position: 'absolute', top: 6, right: 10, fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#fff', letterSpacing: 1, opacity: 0.6 }}>
          {data.counter}
        </span>
      </div>

      {/* Opponent label */}
      <div className="px-4 pb-2">
        <span style={{ fontSize: 10, color: '#64748b', fontFamily: "'DM Mono', monospace", letterSpacing: 1 }}>
          vs {data.opponent.toUpperCase()}
        </span>
      </div>

      {/* Data rows */}
      <div className="px-4 pb-2">
        {rows.map((row, i) => (
          <div key={i} className="flex justify-between items-center py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
            <span style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: 1 }}>{row.label}</span>
            <div className="flex items-center gap-3">
              <span style={{ color: '#fff', fontWeight: 500 }}>{row.value}</span>
              <span style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 4,
                background: row.tagBg, color: row.tagColor, fontWeight: 600
              }}>
                {row.tag}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Confidence bar */}
      <div className="px-4 py-3" style={{ borderTop: '1px dashed rgba(245,158,11,0.15)' }}>
        <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
          <div style={{
            height: '100%', borderRadius: 3,
            background: 'linear-gradient(90deg, #f59e0b, #22c55e)',
            width: `${data.confidence || 85}%`,
            transition: 'width 1.5s ease'
          }}></div>
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
          Confidence: {data.confidence || 85}% &middot; {new Date(data.date).toLocaleDateString()}
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translate(-50%,-50%) scale(0); } to { opacity: 1; transform: translate(-50%,-50%) scale(1); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>
    </div>
  );
};

export default TacticalReadout;
